import React, { useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core'
import { getAdminRenderErrors } from 'ServerAPI'
import Button from 'components/base/Button'
import Input from 'components/base/Input'
import Select from 'components/base/Select'
import MenuItem from 'components/base/MenuItem'
import { CircularProgress } from '~/components/base/CircularProgress/CircularProgress'
import { formatUnixTime } from '~/components/admin/formatUnixTime'
import { usePageSize } from '~/components/admin/usePageSize'
import { ValuePopup, ValuePopupText } from '~/components/admin/ValuePopup'
import { AppRoutes } from '~/AppRoutes'
import './RecentRenderErrors.scss'

const searchByOptions = [
  'projectId',
  'userId',
  'errorType',
  'message',
]

const hasValue = value => value !== null && value !== undefined && value !== ''

const getQueryState = locationSearch => {
  const queryParams = new URLSearchParams(locationSearch)
  const searchByFromQuery = queryParams.get('searchBy') || queryParams.get('sortBy')
  return {
    search: queryParams.get('search') || '',
    searchBy: searchByOptions.includes(searchByFromQuery) ? searchByFromQuery : 'projectId',
  }
}

const getSearchUrl = (searchBy, search) => {
  const queryParams = new URLSearchParams()
  queryParams.set('sortBy', searchBy)
  queryParams.set('search', String(search))
  return `${AppRoutes.AdminRenderErrors}?${queryParams.toString()}`
}

const getSearchValue = (value, searchBy, displayValue = value) => {
  if (!hasValue(value)) return value
  return (
    <Link
      className="admin-panel__link"
      to={getSearchUrl(searchBy, value)}
    >
      {String(displayValue)}
    </Link>
  )
}

const getUserIdValue = value => {
  if (!hasValue(value)) return value
  return (
    <Link
      className="admin-panel__link"
      to={`${AppRoutes.AdminRecentUsers}/${value}`}
    >
      {String(value)}
    </Link>
  )
}

const getProjectIdValue = value => {
  if (!hasValue(value)) return value
  return (
    <a
      className="admin-panel__link"
      href={`${window.location.origin}/projects/${value}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {String(value)}
    </a>
  )
}

export const RecentRenderErrors = () => {
  const { pageSize, tableRef, isReady } = usePageSize()
  const location = useLocation()
  const initialQueryState = getQueryState(location.search)
  const [ searchText, setSearchText ] = useState(initialQueryState.search)
  const [ search, setSearch ] = useState(initialQueryState.search)
  const [ searchBy, setSearchBy ] = useState(initialQueryState.searchBy)
  const [ start, setStart ] = useState(0)
  const [ entries, setEntries ] = useState([])
  const [ totalCount, setTotalCount ] = useState(0)
  const [ messageValue, setMessageValue ] = useState(null)
  const [ loading, setLoading ] = useState(false)

  useEffect(() => {
    const nextQueryState = getQueryState(location.search)
    setStart(0)
    setSearchText(nextQueryState.search)
    setSearch(nextQueryState.search)
    setSearchBy(nextQueryState.searchBy)
  }, [ location.search ])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getAdminRenderErrors({
        start_number: start,
        count: pageSize,
        search,
        searchBy,
      })
      setEntries(data.entries || [])
      setTotalCount(data.totalCount || 0)
    } catch {
      setEntries([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [ start, search, searchBy, pageSize ])

  useEffect(() => {
    if (!isReady) return
    load()
  }, [ load, isReady ])

  const onSearch = () => {
    setStart(0)
    setSearch(searchText)
  }

  const onPage = diff => {
    const nextStart = start + diff * pageSize
    if (nextStart < 0 || nextStart >= totalCount) return
    setStart(nextStart)
  }

  const currentPage = totalCount ? Math.floor(start / pageSize) + 1 : 0
  const totalPages = Math.ceil(totalCount / pageSize)

  return (
    <div className="admin-list">
      <div className="admin-list__search">
        <span className="admin-list__search__label">Search by</span>
        <Select
          className="admin-list__search__select admin-list__search__select--render"
          dropdownClassName="admin-list__search__dropdown--render"
          value={searchBy}
          onChange={e => {
            setSearchBy(e.target.value)
            setStart(0)
          }}
        >
          {searchByOptions.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
        <Input
          id="admin-render-errors-search"
          className="admin-list__search__input"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onEnterKeyDown={onSearch}
        />
        <Button className="admin-list__search__button" primary onClick={onSearch}>
          Search
        </Button>
      </div>

      <div className="admin-list__table" ref={tableRef}>
        <Table
          className="admin-list__table__render"
          aria-label="render errors"
        >
          <TableHead>
            <TableRow>
              <TableCell className="admin-list__table__project-id">projectId</TableCell>
              <TableCell>userId</TableCell>
              <TableCell>errorType</TableCell>
              <TableCell className="admin-list__table__message">message</TableCell>
              <TableCell>createTime</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(entry => (
              <TableRow key={`${entry.projectId}_${entry.createTime}_${entry.errorType}`}>
                <TableCell className="admin-list__table__project-id">
                  {getProjectIdValue(entry.projectId)}
                </TableCell>
                <TableCell>{getUserIdValue(entry.userId)}</TableCell>
                <TableCell>{getSearchValue(entry.errorType, 'errorType')}</TableCell>
                <TableCell className="admin-list__table__message">
                  <ValuePopupText
                    value={entry.message}
                    onClick={() => setMessageValue(entry.message)}
                  />
                </TableCell>
                <TableCell>{formatUnixTime(entry.createTime)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ValuePopup
        title="message"
        value={messageValue}
        open={Boolean(messageValue)}
        onClose={() => setMessageValue(null)}
      />

      <div className="admin-list__footer">
        <If condition={loading}>
          <CircularProgress endless percents={80} transparent size={24} />
        </If>
        <div className="admin-list__footer__nav">
          <Button
            className="admin-list__footer__nav__btn"
            secondary
            onClick={() => onPage(-1)}
            disabled={start === 0}
          >
            Prev
          </Button>
          <Button
            className="admin-list__footer__nav__btn"
            secondary
            onClick={() => onPage(1)}
            disabled={totalCount === 0 || start + pageSize >= totalCount}
          >
            Next
          </Button>
        </div>
        <span className="admin-list__footer__info">
          {'page '}
          {String(currentPage)}
          {' of '}
          {String(totalPages)}
          {', totalCount: '}
          {String(totalCount)}
        </span>
      </div>
    </div>
  )
}
